import { MessageService } from 'primeng/api'
import { Component } from '@angular/core'

import { BankChoice, BankName, BankStatementParseResult, MovementStatus, ReconciledMovement, Verification } from '../../data/interfaces/f098.interface'
import { ApiClientBankAccountsService } from '@modules/private/customer/data/services/api/api-client-bank-accounts.service'
import { entregarDescarga } from '../../data/utils/entregar-descarga.util'
import { ApiF098Service } from '../../data/services/api/api-f098.service'
import { guardarBlobComo } from '@data/utils/descarga-archivo.util'
import { esXls, xlsACsv } from '@data/utils/hoja-a-csv.util'

/** Cómo se lee cada regla en la pantalla. */
const REGLAS: Record<string, string> = {
  CUENTA_ORDENANTE: 'por su cuenta',
  RFC_ORDENANTE: 'por su RFC',
  REFERENCIA: 'por la referencia',
  CLIENTE_Y_MONTO: 'por cliente y monto',
  NINGUNA: '—',
}

const BANCOS: Record<BankName, string> = { COVALTO: 'Covalto', SANTANDER: 'Santander', GENERICO: 'formato genérico' }

/** Las reglas que identifican a la persona con datos del banco: son las que pueden darse de alta. */
const DE_IDENTIDAD = ['CUENTA_ORDENANTE', 'RFC_ORDENANTE']

/**
 * Conciliación bancaria: le pone nombre a cada movimiento del estado de cuenta y dice si ya está
 * registrado en la plataforma (✓ cuadra / diferencia / no aplicado).
 *
 * **No registra ni aplica pagos.** Dice a quién se parece cada movimiento; cobrar sigue siendo del
 * alta de pagos, con su bitácora y su prelación. Un motor que abonara solo convertiría un empate mal
 * resuelto en un asiento contable.
 *
 *! Con el catálogo de cuentas vacío la regla más fuerte no puede correr y casi todo sale «por
 *! identificar». El servidor lo avisa y esta pantalla pinta ese aviso **arriba y en rojo**: sin él, la
 *! primera corrida parecería un fallo del motor en vez de un catálogo sin cargar.
 */
@Component({
  selector: 'app-conciliacion',
  templateUrl: './conciliacion.component.html',
  styleUrls: ['./conciliacion.component.scss'],
})
export class ConciliacionComponent {
  resultado: BankStatementParseResult | null = null
  cargando = false
  archivo = ''
  generando = false
  exportando = false
  /** La CLABE que se está guardando en el catálogo, para que su botón muestre la espera. */
  guardandoCuenta: string | null = null

  /*
   * El archivo que se subió, guardado para poder volver a mandarlo.
   *
   *! La conciliación no persiste nada en el servidor, así que generar la hoja de carga —o volver a
   *! leer tras guardar una cuenta— exige mandar el mismo archivo otra vez. Si era un `.xls`, se guarda
   *! YA convertido: convertirlo dos veces daría lo mismo, pero más lento.
   */
  private ultimoArchivo: File | null = null

  /** `AUTO` por omisión: elegir mal el banco no da un error, da un resultado equivocado que parece bueno. */
  banco: BankChoice = 'AUTO'
  readonly bancos: { value: BankChoice; label: string }[] = [
    { value: 'AUTO', label: 'Detectarlo por el archivo' },
    { value: 'COVALTO', label: 'Covalto' },
    { value: 'SANTANDER', label: 'Santander' },
    { value: 'GENERICO', label: 'Otro' },
  ]

  /** Qué estatus se está mirando. `null` = todos. */
  filtro: MovementStatus | null = null
  /** Y qué verificación. Se combina con el estatus. */
  filtroVerificacion: Verification | null = null

  constructor(
    private readonly api: ApiF098Service,
    private readonly cuentas: ApiClientBankAccountsService,
    private readonly toast: MessageService,
  ) {}

  /** Cuántos movimientos se podrían dar de alta: identificados por su CUENTA o su RFC y sin pago previo. */
  get paraRegistrar(): number {
    return this.todos.filter((m) => m.status === 'IDENTIFICADO' && DE_IDENTIDAD.includes(m.rule) && !m.paymentId).length
  }

  get todos(): ReconciledMovement[] {
    return this.resultado?.reconciliation?.movements || []
  }

  get movimientos(): ReconciledMovement[] {
    return this.todos.filter((m) => (!this.filtro || m.status === this.filtro) && (!this.filtroVerificacion || m.verification === this.filtroVerificacion))
  }

  /** «Santander» o «Santander (detectado)»: quien sube el archivo tiene que saber con qué se leyó. */
  get bancoLeido(): string {
    if (!this.resultado) return ''
    return BANCOS[this.resultado.bank] ?? this.resultado.bank
  }

  get totalCargos(): number {
    return this.todos.filter((m) => m.amount < 0).reduce((s, m) => s + m.amount, 0)
  }

  /*
   * Baja la hoja de carga de pagos con lo que el banco ya dijo.
   *
   * Quita las DOS transcripciones que hay hoy —Finanzas dicta, Operaciones teclea— sin tocar el
   * camino del dinero: el archivo se sube al asistente de «Cargar pagos del día» como cualquier otro
   * y ahí siguen el previo, el aviso de posibles duplicados y la confirmación.
   */
  descargarCarga(): void {
    if (!this.ultimoArchivo || !this.resultado) return
    this.generando = true
    this.api.bankStatementCarga(this.ultimoArchivo, this.resultado.bank).subscribe((blob) => {
      this.generando = false
      if (!blob) return this.toast.add({ severity: 'error', summary: 'Hoja de carga', detail: 'No se pudo generar el archivo' })
      guardarBlobComo(blob, `Carga de pagos ${this.bancoLeido} ${new Date().toISOString().slice(0, 10)}.xlsx`)
    })
  }

  /**
   * La conciliación en .xlsx, con la estructura de la hoja de Contabilidad: los movimientos, el resumen y
   * la verificación contra CENTRAL y contra CARTERA. Se manda el mismo archivo otra vez: esto no guarda nada.
   */
  descargarConciliacion(): void {
    if (!this.ultimoArchivo || !this.resultado) return
    this.exportando = true
    this.api.conciliacionExport(this.ultimoArchivo, this.resultado.bank).subscribe((d) => {
      this.exportando = false
      entregarDescarga(d, `Conciliación ${this.bancoLeido}.xlsx`, this.toast, 'Conciliación')
    })
  }

  /** Lee el archivo. **No escribe nada**: es una lectura con veredicto. */
  async subir(event: any): Promise<void> {
    const elegido: File | undefined = event?.files?.[0] ?? event?.currentFiles?.[0]
    if (!elegido) return
    this.archivo = elegido.name
    let archivo = elegido
    if (esXls(elegido)) {
      try {
        archivo = await xlsACsv(elegido)
      } catch {
        this.toast.add({ severity: 'error', summary: 'Estado de cuenta', detail: `No se pudo abrir ${elegido.name}. Si el banco lo da en otro formato (.xlsx o .csv), súbelo así.`, life: 8000 })
        return
      }
    }
    this.ultimoArchivo = archivo
    this.leer(true)
  }

  /**
   * `nuevo`: es otro archivo y se empieza de cero. Si solo se RELEE —tras guardar una cuenta— se
   * conservan los filtros: quien estaba mirando los identificados no tiene por qué perderlos.
   */
  private leer(nuevo: boolean): void {
    if (!this.ultimoArchivo) return
    this.cargando = true
    this.resultado = null
    if (nuevo) {
      this.filtro = null
      this.filtroVerificacion = null
    }

    this.api.bankStatement(this.ultimoArchivo, this.banco).subscribe((r) => {
      this.cargando = false
      if (r.error || !r.data) return this.toast.add({ severity: 'error', summary: 'Estado de cuenta', detail: r.msg, life: 8000 })
      this.resultado = r.data
      const t = r.data.reconciliation.totals
      if (nuevo)
        this.toast.add({
          severity: 'success',
          summary: `Estado de cuenta de ${this.bancoLeido} leído`,
          detail: `${t.identified} identificados de ${t.identified + t.unidentified} ingresos de clientes`,
        })
    })
  }

  /*
   * Guarda en el catálogo la cuenta de un movimiento que se identificó por el RFC.
   *
   * El RFC identificó a la persona; la CLABE es desde dónde paga. Guardarla hace que el próximo
   * estado de cuenta —también el de Covalto, que no da RFC— lo reconozca por su cuenta. Es la forma
   * de llenar el catálogo con los datos que ya da el banco en vez de capturarlos a mano.
   */
  guardarCuenta(m: ReconciledMovement): void {
    if (!m.originClabe || !m.clientId) return
    this.guardandoCuenta = m.originClabe
    this.cuentas.create({ clabe: m.originClabe, client: m.clientId, holderName: m.originName }).subscribe((r) => {
      this.guardandoCuenta = null
      if (r.error) return this.toast.add({ severity: 'error', summary: 'Catálogo de cuentas', detail: r.msg, life: 8000 })
      this.toast.add({ severity: 'success', summary: 'Cuenta guardada', detail: `${m.originClabe} ya es de ${m.clientName}. Se vuelve a leer el estado de cuenta.` })
      this.leer(false)
    })
  }

  puedeGuardarCuenta(m: ReconciledMovement): boolean {
    return m.status === 'IDENTIFICADO' && m.rule !== 'CUENTA_ORDENANTE' && !!m.originClabe && !!m.clientId
  }

  otroArchivo(): void {
    this.resultado = null
    this.ultimoArchivo = null
    this.archivo = ''
  }

  cuantos(estatus: MovementStatus): number {
    return this.todos.filter((m) => m.status === estatus).length
  }

  alternar(estatus: MovementStatus): void {
    this.filtro = this.filtro === estatus ? null : estatus
  }

  alternarVerificacion(v: Verification): void {
    this.filtroVerificacion = this.filtroVerificacion === v ? null : v
  }

  regla(m: ReconciledMovement): string {
    return REGLAS[m.rule] ?? m.rule
  }

  severidad(estatus: MovementStatus): string {
    if (estatus === 'IDENTIFICADO') return 'success'
    if (estatus === 'POR_IDENTIFICAR') return 'danger'
    return estatus === 'INTERNO_TRASPASO' ? 'info' : 'warning'
  }

  etiqueta(estatus: MovementStatus): string {
    return { IDENTIFICADO: 'identificado', POR_IDENTIFICAR: 'por identificar', INTERNO_TRASPASO: 'traspaso interno', EGRESO: 'egreso' }[estatus] || estatus
  }

  etiquetaVerificacion(v: Verification): string {
    return { CUADRA: '✓ cuadra', DIFERENCIA: 'diferencia', NO_APLICADO: 'no aplicado' }[v] || v
  }

  severidadVerificacion(v: Verification): string {
    return { CUADRA: 'success', DIFERENCIA: 'warning', NO_APLICADO: 'danger' }[v] || 'info'
  }

  /** El porcentaje de identificados. `null` es «no aplica», no «0 %». */
  get tasa(): string {
    const r = this.resultado?.reconciliation?.totals?.identifiedRate
    return r == null ? 'n/a' : `${Math.round(r * 1000) / 10}%`
  }

  /** El aviso del catálogo vacío, si vino. Es el que más importa de los tres. */
  get avisoCatalogo(): string | null {
    return (this.resultado?.reconciliation?.caveats || []).find((c) => c.includes('VACÍO')) ?? null
  }
}
